/**
 * Legt de weergavelaag van een organisatie over een canonieke statuslijst.
 *
 * De statuslijsten zelf zijn gedeeld en veranderen niet per organisatie
 * (ADR-032): de portfolio-optelling telt statussen óver organisaties heen op,
 * en een eigen lijst maakt die optelling betekenisloos. Wat een organisatie
 * wél mag, is een status een eigen naam geven en een status die ze niet
 * gebruikt uit beeld halen.
 *
 * **De grens.** Deze helper hoort alleen thuis waar een mens leest: tabs,
 * keuzelijsten, kolomkoppen, kaarten. Nooit in een telling, een grafiek, de
 * portfolio-optelling, de statushistorie of de datakwaliteitsscore — een
 * verborgen status blijft daar gewoon meetellen. Verbergen is een gordijn,
 * geen filter.
 *
 * AI-CONTEXT: See docs/decisions.md#adr-032
 * AI-CONTEXT: See docs/features/tenant-status-and-column-config.md
 */

/**
 * @typedef {object} StatusSetting
 * @property {string} [Label] - Eigen woord van de organisatie; leeg = canoniek
 * @property {boolean|string} [Hidden] - `true` of `'true'` haalt de status uit beeld
 */

/**
 * @typedef {object} StatusDisplay
 * @property {Record<string, string>} labels - Per sleutel het label dat de lezer ziet
 * @property {Array<string>} keys - Élke canonieke sleutel, in trechtervolgorde
 * @property {Array<string>} visibleKeys - Wat er getoond mag worden, zelfde volgorde
 */

/**
 * `ToggleButton` schrijft de string `'true'`/`'false'`, `useSettings` geeft
 * soms een echte boolean terug, en een document dat de instelling nooit gezien
 * heeft geeft `undefined`. Alles wat geen van beide vormen van "ja" is telt als
 * zichtbaar — de brede kant, want dit is een versmalling.
 *
 * AI-CONTEXT: See docs/pitfalls.md#ui-togglebutton-string-only-branches
 *
 * @param {*} value
 * @returns {boolean}
 */
const isHidden = (value) => value === true || value === 'true'

/**
 * Of de instelling élke status zou verbergen.
 *
 * Geëxporteerd omdat het beheerscherm dit ook moet weten (voor de waarschuwing)
 * en `resolveStatusDisplay` het geval negeert. Zouden die twee het los
 * uitrekenen, dan kan de melding verschijnen terwijl er wél verborgen wordt —
 * of omgekeerd, zonder dat een test dat ziet.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @param {Record<string, string>} canonicalLabels
 * @param {Record<string, StatusSetting>} [config]
 * @returns {boolean}
 */
const allStatusesHidden = (canonicalLabels, config) => {
    const keys = Object.keys(canonicalLabels || {})
    if (keys.length === 0) return false
    const settings = (config && typeof config === 'object' && !Array.isArray(config)) ? config : {}
    return keys.every((key) => isHidden(settings[key] && settings[key].Hidden))
}

/**
 * Het label dat de organisatie instelde, of het canonieke woord.
 *
 * Bewust geen afkapping op lengte: het invoerveld begrenst de lengte, en hier
 * alsnog knippen zou betekenen dat de instelling iets anders toont dan de tab
 * — twee waarheden voor dezelfde tekst.
 * AI-CONTEXT: See docs/pitfalls.md#dev-helper-lossy-on-input-it-claims-to-accept
 *
 * @param {StatusSetting} [setting]
 * @param {string} canonical
 * @returns {string}
 */
const labelFor = (setting, canonical) => {
    const own = setting && setting.Label
    if (typeof own !== 'string') return canonical
    const trimmed = own.trim()
    return trimmed === '' ? canonical : trimmed
}

/**
 * @param {Record<string, string>} canonicalLabels - `ADRESS_STATUS_LABELS` of `PERSONA_STATUS_LABELS`
 * @param {Record<string, StatusSetting>} [config] - `CompagnyMeta.AdressStatusConfig` / `PersonaStatusConfig`
 * @returns {StatusDisplay}
 */
const resolveStatusDisplay = (canonicalLabels, config) => {
    const canonical = canonicalLabels || {}
    const keys = Object.keys(canonical)

    // Alles wat geen gewone map is — nog niet geladen, een mislukte read, een
    // array, een string — leest als "geen instelling". Zie
    // pitfalls.md#state-gate-defaults-open-while-loading: de terugval moet hier
    // de BREDE kant kiezen, want niet-tonen is de versmalling.
    const settings = (config && typeof config === 'object' && !Array.isArray(config))
        ? config
        : {}

    const labels = keys.reduce((acc, key) => {
        acc[key] = labelFor(settings[key], canonical[key])
        return acc
    }, {})

    const visible = keys.filter((key) => !isHidden(settings[key] && settings[key].Hidden))

    return {
        labels,
        keys,
        // Álles verbergen laat een scherm achter waarop niemand nog een status
        // kan kiezen. Dat bedoelt niemand, dus lezen we het als een ongeldige
        // instelling in plaats van het uit te voeren.
        visibleKeys: visible.length === 0 ? keys : visible,
    }
}

/**
 * De opties voor een keuzelijst: de zichtbare statussen, plus de status die er
 * nú op staat ook als die verborgen is.
 *
 * Dat laatste is ADR-031 regel 4 en het is geen nettigheid: rendert de
 * `<option>` van de opgeslagen waarde niet, dan toont de lijst blanco en
 * schrijft één `onChange` een andere status weg dan er stond. Een waarde die
 * de code helemaal niet meer kent krijgt zichzelf als label — lelijk, maar de
 * gebruiker ziet tenminste wat er staat in plaats van een leeg vak.
 *
 * @param {StatusDisplay} display - Uitkomst van `resolveStatusDisplay`
 * @param {string} [currentKey] - De status die het document nu draagt
 * @returns {Array<{value: string, label: string}>}
 */
const statusOptionsFor = (display, currentKey) => {
    const { labels = {}, keys = [], visibleKeys = [] } = display || {}
    const shown = new Set(visibleKeys)

    if (currentKey && !shown.has(currentKey)) {
        // Bekende sleutel: terug op zijn eigen plek in de trechter, zodat de
        // lijst niet ineens een andere volgorde heeft dan de tabs ernaast.
        if (keys.includes(currentKey)) {
            shown.add(currentKey)
        } else {
            // Onbekende sleutel: geen plek in de trechter, dus achteraan.
            return [
                ...keys.filter((key) => shown.has(key))
                    .map((key) => ({ value: key, label: labels[key] })),
                { value: currentKey, label: currentKey },
            ]
        }
    }

    return keys
        .filter((key) => shown.has(key))
        .map((key) => ({ value: key, label: labels[key] }))
}

export { statusOptionsFor, isHidden, allStatusesHidden }
export default resolveStatusDisplay
